import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { apiBase } from "@/lib/apiBase";
import { WAITLIST_CONSENT_VERSION, WAITLIST_COPY } from "@/constants/waitlistCopy";

const API = apiBase();
const PLANS = new Set(["professional", "firm"]);

function fieldError(name, email, firmName, role, consent) {
    const trimmedName = name.trim();
    const trimmedFirm = firmName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || trimmedName.length > 120) return true;
    if (!trimmedFirm || trimmedFirm.length > 120) return true;
    if (!trimmedEmail || !trimmedEmail.includes("@") || trimmedEmail.length > 200) return true;
    if (!WAITLIST_COPY.roles.includes(role)) return true;
    if (!consent) return true;
    return false;
}

export default function WaitlistForm({ plan }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [firmName, setFirmName] = useState("");
    const [role, setRole] = useState("");
    const [consent, setConsent] = useState(false);
    const [website, setWebsite] = useState("");
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [pending, setPending] = useState(false);
    const sending = useRef(false);

    if (!PLANS.has(plan)) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (sending.current) return;
        if (fieldError(name, email, firmName, role, consent)) {
            setError(WAITLIST_COPY.errorInvalid);
            return;
        }
        sending.current = true;
        setPending(true);
        setError("");
        try {
            await axios.post(`${API}/waitlist`, {
                name: name.trim(),
                email: email.trim(),
                firm_name: firmName.trim(),
                role,
                plan,
                consent: true,
                consent_text_version: WAITLIST_CONSENT_VERSION,
                website,
            });
            setDone(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) setError(WAITLIST_COPY.errorRate);
            else if (status === 422 || status === 413) setError(WAITLIST_COPY.errorInvalid);
            else setError(WAITLIST_COPY.errorGeneric);
        } finally {
            sending.current = false;
            setPending(false);
        }
    };

    return (
        <section className="mt-6" data-testid={`waitlist-${plan}`}>
            <h3 className="text-base font-semibold text-white">{WAITLIST_COPY.headline}</h3>
            <p className="mt-2 text-sm leading-snug text-slate-400">{WAITLIST_COPY.detail}</p>
            {done ? (
                <>
                    <p
                        role="status"
                        data-testid={`waitlist-success-${plan}`}
                        className="mt-4 text-sm font-semibold text-white"
                    >
                        {WAITLIST_COPY.success}
                    </p>
                    <p
                        className="mt-3 text-sm text-slate-400"
                        data-testid={`waitlist-remove-${plan}`}
                    >
                        {WAITLIST_COPY.removeLine}
                    </p>
                </>
            ) : (
                <form
                    className="relative mt-4"
                    data-testid={`waitlist-form-${plan}`}
                    onSubmit={submit}
                    noValidate
                >
                    <div
                        aria-hidden="true"
                        className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]"
                    >
                        <input
                            type="text"
                            name="website"
                            tabIndex={-1}
                            autoComplete="off"
                            value={website}
                            onChange={(event) => setWebsite(event.target.value)}
                            data-testid={`waitlist-honeypot-${plan}`}
                        />
                    </div>
                    <label className="block text-sm text-slate-300">
                        {WAITLIST_COPY.nameLabel}
                        <input
                            type="text"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            autoComplete="name"
                            maxLength={120}
                            className="fsrs-input mt-1"
                            data-testid={`waitlist-name-${plan}`}
                        />
                    </label>
                    <label className="mt-3 block text-sm text-slate-300">
                        {WAITLIST_COPY.emailLabel}
                        <input
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            className="fsrs-input mt-1"
                            data-testid={`waitlist-email-${plan}`}
                        />
                    </label>
                    <label className="mt-3 block text-sm text-slate-300">
                        {WAITLIST_COPY.firmLabel}
                        <input
                            type="text"
                            value={firmName}
                            onChange={(event) => setFirmName(event.target.value)}
                            autoComplete="organization"
                            maxLength={120}
                            className="fsrs-input mt-1"
                            data-testid={`waitlist-firm-${plan}`}
                        />
                    </label>
                    <label className="mt-3 block text-sm text-slate-300">
                        {WAITLIST_COPY.roleLabel}
                        <select
                            value={role}
                            onChange={(event) => setRole(event.target.value)}
                            className="fsrs-input mt-1"
                            data-testid={`waitlist-role-${plan}`}
                        >
                            <option value="" />
                            {WAITLIST_COPY.roles.map((item) => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </select>
                    </label>
                    <label className="mt-4 flex items-start gap-3 text-sm leading-snug text-slate-300">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(event) => setConsent(event.target.checked)}
                            className="mt-1 h-6 w-6 shrink-0 rounded border-white/20 bg-white/5 text-red-600"
                            data-testid={`waitlist-consent-${plan}`}
                        />
                        <span>
                            {WAITLIST_COPY.consent}{" "}
                            {WAITLIST_COPY.termsLead}
                            <Link
                                to="/terms"
                                target="_blank"
                                className="underline"
                                data-testid={`waitlist-terms-${plan}`}
                            >
                                {WAITLIST_COPY.terms}
                            </Link>
                            {WAITLIST_COPY.termsEnd}
                        </span>
                    </label>
                    <p
                        className="mt-3 text-sm text-slate-500"
                        data-testid={`waitlist-remove-${plan}`}
                    >
                        {WAITLIST_COPY.removeLine}
                    </p>
                    <p
                        className="mt-2 text-sm text-slate-500"
                        data-testid={`waitlist-rate-${plan}`}
                    >
                        {WAITLIST_COPY.rateLimit}
                    </p>
                    {error ? (
                        <p
                            role="alert"
                            data-testid={`waitlist-error-${plan}`}
                            className="mt-3 text-sm text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}
                    <button
                        type="submit"
                        disabled={pending}
                        onClick={submit}
                        data-testid={`waitlist-submit-${plan}`}
                        className="mt-4 w-full border border-red-500 py-3 text-red-500 font-bold hover:bg-red-500/10 disabled:opacity-60"
                    >
                        {WAITLIST_COPY.submit}
                    </button>
                </form>
            )}
        </section>
    );
}
